import { useNavigate } from "@solidjs/router";
import { BiSolidUserAccount } from "solid-icons/bi";
import { type Component, createMemo, createSignal, For, Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import { AvatarToDeprecate } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  MAX_SELECTION,
  useFreelanceSearchStore,
} from "../store/freelance-search-store";

export const FreelancerSelectionFloatingBar: Component = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [store, setStore] = useFreelanceSearchStore();
  const [hoveredId, setHoveredId] = createSignal<string | null>(null);

  const selected = () => store.selectedFreelancers;
  const remaining = () => MAX_SELECTION - selected().length;

  const slots = createMemo(() =>
    Array.from({ length: MAX_SELECTION }, (_, i) => selected()[i] ?? null),
  );

  const removeFreelancer = (id: string) => {
    setStore("selectedFreelancers", (current) =>
      current.filter((f) => f.id !== id),
    );
  };

  const handleContact = () => {
    if (store.jobOfferId) {
      navigate("/profile/company/search/freelancers/send-proposal");
    } else {
      navigate("/profile/company/search/freelancers/new-proposal");
    }
  };

  return (
    <div class="fixed top-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-4 px-5 py-3 bg-white border border-gray-200 rounded-full shadow-lg">
      {/* Left: label + slots info */}
      <div class="flex flex-col">
        <span class="text-xs font-semibold text-gray-800">
          {t("company.search.freelancers.result.floating_bar.label")}
        </span>
        <span class="text-xs text-gray-500">
          {t("company.search.freelancers.result.floating_bar.slots_label", {
            selected: selected().length,
            remaining: remaining(),
          })}
        </span>
      </div>

      {/* Center: 4 avatar slots */}
      <div class="flex items-center gap-1.5">
        <For each={slots()}>
          {(freelancer) => (
            <Show
              when={freelancer}
              fallback={
                <div class="h-9 w-9 rounded-full border-2 border-dashed border-gray-300" />
              }
            >
              {(f) => (
                <button
                  type="button"
                  class="relative h-9 w-9"
                  onMouseEnter={() => setHoveredId(f().id)}
                  onMouseLeave={() => setHoveredId(null)}
                  onClick={() => removeFreelancer(f().id)}
                >
                  <AvatarToDeprecate
                    src={f().avatar_url}
                    alt={f().full_name}
                    class="h-9 w-9"
                    fallback={<BiSolidUserAccount class="w-5 h-5" />}
                  />
                  <Show when={hoveredId() === f().id}>
                    <span class="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 text-white text-base font-bold">
                      ×
                    </span>
                  </Show>
                </button>
              )}
            </Show>
          )}
        </For>
      </div>

      {/* Right: contact button */}
      <Button size="sm" onClick={handleContact}>
        {t("company.search.freelancers.result.floating_bar.contact")} ›
      </Button>
    </div>
  );
};
